import { Checkbox, Grid, Select, TextInput } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { UseFormReturnType } from "@mantine/form/lib/use-form";
import { useEffect, useState } from "react";
import { Calendar } from "tabler-icons-react";
import { cities, provinces } from "../../../../data/province-city.array";
import { Cities } from "../../../../type/province-city.types";
import { formatDate } from "../../../../helpers/date-convert";
import { UpdateMerchantOnboardingFormValues } from "../../../../type/update-merchant-onboarding-form-values.types";
import PhoneCountryCodeIcon from "../../../icons/phone-country-code-icon.component";
import { BusinessTypes } from "../../../../enums/business-type";

interface UpdateIncorporatorProps {
  form: UseFormReturnType<UpdateMerchantOnboardingFormValues>;
  index: number;
}
function UpdateIncorporator(props: UpdateIncorporatorProps) {
  const { form, index } = props;
  const [newProvinces, setNewProvinces] = useState(provinces);
  const [newCities, setNewCities] = useState(cities);

  useEffect(() => {
    if (
      !provinces.some((province: { value: string; label: string }) => {
        if (province.value === form.values.incorporators![index].province) {
          return true;
        }
        return false;
      })
    ) {
      setNewProvinces([
        ...newProvinces,
        {
          key: form.values.incorporators![index].province,
          value: form.values.incorporators![index].province,
          label: form.values.incorporators![index].province,
        },
      ]);
      const newProvince = {
        [form.values.incorporators![index].province]: [
          {
            key: form.values.incorporators![index].city,
            value: form.values.incorporators![index].city,
            label: form.values.incorporators![index].city,
          },
        ],
      };
      const newCities: Cities = {
        ...cities,
        ...newProvince,
      };
      setNewCities(newCities);
    } else if (
      !cities[form.values.incorporators![index].province].some(
        (city: { value: string; label: string }) => {
          if (city.value === form.values.incorporators![index].city) {
            return true;
          }
          return false;
        }
      )
    ) {
      setNewCities({
        ...newCities,
        [form.values.incorporators![index].province]: [
          ...newCities[form.values.incorporators![index].province],
          {
            key: form.values.incorporators![index].city,
            value: form.values.incorporators![index].city,
            label: form.values.incorporators![index].city,
          },
        ],
      });
    }
  }, []);

  const label = {
    incorporatorFirstName: "First name",
    incorporatorMiddleName: "Middle name",
    incorporatorHasMiddleName: "I don't have a middle name",
    incorporatorLastName: "Last name",
    incorporatorEmailAddress: "Email address",
    incorporatorContactNumber: "Contact number",
    incorporatorBirthDate: "Birthdate",
    incorporatorBldgNameNo: "Building name and number",
    incorporatorResidentialAddress: "Residential address",
    incorporatorBarangay: "Barangay",
    incorporatorCity: "City",
    incorporatorProvince: "Province",
    incorporatorPostalCode: "Postal code",
  };
  const placeHolder = {
    incorporatorFirstName: "First name",
    incorporatorMiddleName: "Middle name",
    incorporatorLastName: "Last name",
    incorporatorEmailAddress: "Email address",
    incorporatorContactNumber: "Contact number",
    incorporatorBirthDate: "Select birthdate",
    incorporatorBldgNameNo: "Building name and number",
    incorporatorResidentialAddress: "Street address",
    incorporatorBarangay: "Barangay",
    incorporatorCity: "City",
    incorporatorProvince: "Province",
    incorporatorPostalCode: "Postal code",
  };

  useEffect(() => {
    const hasErrors = Object.values(form.errors).length > 0;
    if (hasErrors) {
      form.validate();
    }
  }, [form.values.incorporators]);

  return (
    <>
      {form.values.incorporators && (
        <>
          <Grid>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.incorporatorFirstName}
                label={label.incorporatorFirstName}
                onChange={(event) => {
                  form.setListItem("incorporators", index, {
                    ...form.values.incorporators![index],
                    firstName: event.currentTarget.value,
                  });
                  if (
                    form.values.businessTypeId === BusinessTypes.SOLE_PROPRIETOR
                  ) {
                    if (form.values.copyProToSignatory) {
                      form.setListItem("signatories", 0, {
                        ...form.values.signatories[0],
                        firstName: event.currentTarget.value,
                      });
                    }
                    if (form.values.copyProToRep) {
                      form.setFieldValue(
                        "aFirstName",
                        event.currentTarget.value
                      );
                    }
                  }
                }}
                value={
                  form.getListInputProps("incorporators", index, "firstName")
                    .value
                }
                error={
                  form.getListInputProps("incorporators", index, "firstName")
                    .error
                }
                data-cy="firstName"
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.incorporatorMiddleName}
                label={label.incorporatorMiddleName}
                disabled={!form.values.incorporators[index].hasMiddleName}
                onChange={(event) => {
                  form.setListItem("incorporators", index, {
                    ...form.values.incorporators![index],
                    middleName: event.currentTarget.value,
                  });
                  if (
                    form.values.businessTypeId === BusinessTypes.SOLE_PROPRIETOR
                  ) {
                    if (form.values.copyProToSignatory) {
                      form.setListItem("signatories", 0, {
                        ...form.values.signatories[0],
                        middleName: event.currentTarget.value,
                      });
                    }
                    if (form.values.copyProToRep) {
                      form.setFieldValue(
                        "aMiddleName",
                        event.currentTarget.value
                      );
                    }
                  }
                }}
                value={
                  form.getListInputProps("incorporators", index, "middleName")
                    .value
                }
                error={
                  form.getListInputProps("incorporators", index, "middleName")
                    .error
                }
                data-cy="middleName"
              />
            </Grid.Col>
            <Grid.Col>
              <Checkbox
                label={label.incorporatorHasMiddleName}
                checked={!form.values.incorporators[index].hasMiddleName}
                onChange={(event) => {
                  form.setListItem("incorporators", index, {
                    ...form.values.incorporators![index],
                    hasMiddleName: !event.currentTarget.checked,
                    middleName: event.currentTarget.checked ? "N/A" : "",
                  });
                  if (
                    form.values.businessTypeId === BusinessTypes.SOLE_PROPRIETOR
                  ) {
                    if (form.values.copyProToSignatory) {
                      form.setListItem("signatories", 0, {
                        ...form.values.signatories[index],
                        hasMiddleName: !event.currentTarget.checked,
                        middleName: event.currentTarget.checked ? "N/A" : "",
                      });
                    }
                    if (form.values.copyProToRep) {
                      form.setFieldValue(
                        "isAMNameDisabled",
                        !event.currentTarget.checked
                      );
                      form.setFieldValue(
                        "aMiddleName",
                        !event.currentTarget.checked ? "" : "N/A"
                      );
                    }
                  }
                }}
                data-cy="hasMiddleName"
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.incorporatorLastName}
                label={label.incorporatorLastName}
                onChange={(event) => {
                  form.setListItem("incorporators", index, {
                    ...form.values.incorporators![index],
                    lastName: event.currentTarget.value,
                  });
                  if (
                    form.values.businessTypeId === BusinessTypes.SOLE_PROPRIETOR
                  ) {
                    if (form.values.copyProToSignatory) {
                      form.setListItem("signatories", 0, {
                        ...form.values.signatories[0],
                        lastName: event.currentTarget.value,
                      });
                    }
                    if (form.values.copyProToRep) {
                      form.setFieldValue(
                        "aLastName",
                        event.currentTarget.value
                      );
                    }
                  }
                }}
                value={
                  form.getListInputProps("incorporators", index, "lastName")
                    .value
                }
                error={
                  form.getListInputProps("incorporators", index, "lastName")
                    .error
                }
                data-cy="lastName"
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.incorporatorEmailAddress}
                label={label.incorporatorEmailAddress}
                onChange={(event) => {
                  form.setListItem("incorporators", index, {
                    ...form.values.incorporators![index],
                    emailAddress: event.currentTarget.value,
                  });
                  if (
                    form.values.businessTypeId === BusinessTypes.SOLE_PROPRIETOR
                  ) {
                    if (form.values.copyProToRep) {
                      form.setFieldValue(
                        "aEmailAddress",
                        event.currentTarget.value
                      );
                    }
                  }
                }}
                value={
                  form.getListInputProps("incorporators", index, "emailAddress")
                    .value
                }
                error={
                  form.getListInputProps("incorporators", index, "emailAddress")
                    .error
                }
                data-cy="emailAddress"
              />
            </Grid.Col>
            <Grid.Col xs={12} md={6}>
              <TextInput
                icon={<PhoneCountryCodeIcon />}
                iconWidth={48}
                label={label.incorporatorContactNumber}
                onChange={(event) => {
                  form.setListItem("incorporators", index, {
                    ...form.values.incorporators![index],
                    contactNumber: event.currentTarget.value,
                  });
                  if (
                    form.values.businessTypeId === BusinessTypes.SOLE_PROPRIETOR
                  ) {
                    if (form.values.copyProToRep) {
                      form.setFieldValue(
                        "aContactNumber",
                        event.currentTarget.value
                      );
                    }
                  }
                }}
                value={
                  form.getListInputProps(
                    "incorporators",
                    index,
                    "contactNumber"
                  ).value
                }
                error={
                  form.getListInputProps(
                    "incorporators",
                    index,
                    "contactNumber"
                  ).error
                }
                data-cy="contactNumber"
              />
            </Grid.Col>
            <Grid.Col xs={12} md={6}>
              <DatePicker
                icon={<Calendar />}
                placeholder={placeHolder.incorporatorBirthDate}
                label={label.incorporatorBirthDate}
                onChange={(value: Date) => {
                  form.setListItem("incorporators", index, {
                    ...form.values.incorporators![index],
                    birthDate: value,
                  });
                  if (
                    form.values.businessTypeId === BusinessTypes.SOLE_PROPRIETOR
                  ) {
                    if (form.values.copyProToRep) {
                      form.setFieldValue("aDateOfBirth", value);
                    }
                  }
                }}
                value={
                  form.getListInputProps("incorporators", index, "birthDate")
                    .value
                }
                error={
                  form.getListInputProps("incorporators", index, "birthDate")
                    .error
                }
                data-cy="birthDate"
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.incorporatorBldgNameNo}
                label={label.incorporatorBldgNameNo}
                {...form.getListInputProps(
                  "incorporators",
                  index,
                  "bldgNameNo"
                )}
                data-cy="bldgNameNo"
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.incorporatorResidentialAddress}
                label={label.incorporatorResidentialAddress}
                {...form.getListInputProps(
                  "incorporators",
                  index,
                  "residentialAddress"
                )}
                data-cy="residentialAddress"
              />
            </Grid.Col>
            <Grid.Col xs={12} md={6}>
              <Select
                label={label.incorporatorProvince}
                placeholder={placeHolder.incorporatorProvince}
                data={newProvinces}
                searchable
                nothingFound="No options"
                onChange={(value: string) => {
                  form.setListItem("incorporators", index, {
                    ...form.values.incorporators![index],
                    province: value,
                    city: "",
                  });
                }}
                error={
                  form.getListInputProps("incorporators", index, "province")
                    .error
                }
                value={
                  form.getListInputProps("incorporators", index, "province")
                    .value
                }
                data-cy="province"
              />
            </Grid.Col>
            <Grid.Col xs={12} md={6}>
              <Select
                label={label.incorporatorCity}
                placeholder={placeHolder.incorporatorCity}
                data={
                  form.values.incorporators[index].province.length > 0 &&
                  Object.keys(newCities).includes(
                    form.values.incorporators[index].province
                  )
                    ? newCities[form.values.incorporators[index].province]
                    : []
                }
                disabled={form.values.incorporators[index].province === ""}
                searchable
                nothingFound="No options"
                {...form.getListInputProps("incorporators", index, "city")}
                data-cy="city"
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6}>
              <TextInput
                placeholder={placeHolder.incorporatorBarangay}
                label={label.incorporatorBarangay}
                {...form.getListInputProps("incorporators", index, "barangay")}
                data-cy="barangay"
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6}>
              <TextInput
                placeholder={placeHolder.incorporatorPostalCode}
                label={label.incorporatorPostalCode}
                {...form.getListInputProps(
                  "incorporators",
                  index,
                  "postalCode"
                )}
                data-cy="postalCode"
              />
            </Grid.Col>
          </Grid>
        </>
      )}
    </>
  );
}

export default UpdateIncorporator;
